import { CHAT_MODE_MODELS } from './chat-events'

/**
 * OpenRouter provider-routing preferences, per model slug — sent as the
 * `provider` object of the chat-completions request by the provider wrapper
 * (`worker/llm/provider-api.ts`, the one place that shapes an outgoing
 * request). Docs: openrouter.ai/docs/guides/routing/provider-selection and
 * /guides/best-practices/latency-and-performance.
 *
 * Only models that OpenRouter serves from *several* providers get an entry.
 * Today that is the normal-mode GLM 5.3 Flash: 25 endpoints (2026-09-11),
 * fp4/fp8/unknown quantisations, and the 12-minute stall PLAN v2 §1 traced to
 * one slow endpoint. The Claude models (high/max) are served by Anthropic,
 * Bedrock, Vertex and Azure (2026-10-29) without declared quantisations, so a
 * quantisation filter would match nothing and OpenRouter's default price-first
 * routing is what we want there.
 *
 * - `quantizations` is a **filter**: only endpoints with a matching, declared
 *   quantisation are eligible. fp8 excludes the fp4 endpoints and the ones that
 *   declare none. Check `GET /api/v1/models/<slug>/endpoints` before adding a
 *   level nobody serves — an empty match means a failed request.
 * - `preferred_max_latency` / `preferred_min_throughput` are **soft**: OpenRouter
 *   prefers endpoints under 2.5 s p90 time-to-first-token and over 40 tok/s p90
 *   (rolling 5-minute window) but never refuses the request over them.
 */
export interface OpenRouterProviderPreferences {
  quantizations?: string[]
  preferred_max_latency?: { p50?: number; p75?: number; p90?: number; p99?: number }
  preferred_min_throughput?: { p50?: number; p75?: number; p90?: number; p99?: number }
}

export const OPENROUTER_PROVIDER_PREFERENCES: Readonly<
  Record<string, OpenRouterProviderPreferences>
> = {
  [CHAT_MODE_MODELS.normal]: {
    quantizations: ['fp8'],
    preferred_max_latency: { p90: 2.5 },
    preferred_min_throughput: { p90: 40 },
  },
}

/** Preferences for a model slug, with or without the `openrouter/` prefix. */
export function openRouterProviderPreferences(
  model: string
): OpenRouterProviderPreferences | undefined {
  const slug = model.startsWith('openrouter/') ? model.slice('openrouter/'.length) : model
  return OPENROUTER_PROVIDER_PREFERENCES[slug]
}
